import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Layers,
  Plus,
  BarChart3,
  TrendingUp,
  DollarSign,
  Eye,
  MousePointer,
  Target,
  LogOut,
  Settings,
  ChevronDown,
  Zap,
  Play,
  Pause,
  Trash2,
  Edit,
  Copy,
  Calendar,
  X,
  Bell,
  BellRing,
  Webhook
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdvertiserDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [fundAmount, setFundAmount] = useState(100);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [selectedCampaignForAlert, setSelectedCampaignForAlert] = useState("");

  const [newAlert, setNewAlert] = useState({
    alert_type: "budget_depleted",
    threshold: 80,
    webhook_url: "",
    email_enabled: true
  });

  const [newCampaign, setNewCampaign] = useState({
    name: "",
    budget: 100,
    daily_budget: 10,
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    bid_type: "cpm",
    bid_amount: 5,
    targeting: { devices: ["desktop", "mobile"], geo: [] },
    status: "draft"
  });

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    
    if (sessionId && paymentStatus === "success") {
      try {
        const pollStatus = async (attempts = 0) => {
          if (attempts >= 5) {
            toast.info("Payment is being processed. Balance will update shortly.");
            return;
          }
          
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            toast.success(`$${response.data.amount} added to your balance!`);
            await refreshUser();
            navigate("/dashboard", { replace: true });
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        };
        
        await pollStatus();
      } catch (error) {
        console.error("Payment status check error:", error);
      }
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled");
      navigate("/dashboard", { replace: true });
    }
  };

  const fetchData = async () => {
    try {
      const [analyticsRes, campaignsRes, templatesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/analytics/advertiser`),
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/alerts`)
      ]);
      setAnalytics(analyticsRes.data);
      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/campaigns`, newCampaign);
      setCampaigns([response.data, ...campaigns]);
      setShowCreateModal(false);
      toast.success("Campaign created successfully!");
      setNewCampaign({
        name: "",
        budget: 100,
        daily_budget: 10,
        start_date: new Date().toISOString().split('T')[0],
        end_date: "",
        bid_type: "cpm",
        bid_amount: 5,
        targeting: { devices: ["desktop", "mobile"], geo: [] },
        status: "draft"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const handleUpdateStatus = async (campaignId, status) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}`, { status });
      setCampaigns(campaigns.map(c => c.id === campaignId ? { ...c, status } : c));
      toast.success(`Campaign ${status === "active" ? "activated" : "paused"}`);
    } catch (error) {
      toast.error("Failed to update campaign");
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      toast.success("Campaign deleted");
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const handleFundAccount = async () => {
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        amount: parseFloat(fundAmount),
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    if (!selectedCampaignForAlert) {
      toast.error("Please select a campaign");
      return;
    }
    try {
      const response = await axios.post(`${API}/alerts`, {
        campaign_id: selectedCampaignForAlert,
        ...newAlert
      });
      setAlerts([response.data, ...alerts]);
      setShowAlertModal(false);
      toast.success("Alert created successfully!");
      setNewAlert({
        alert_type: "budget_depleted",
        threshold: 80,
        webhook_url: "",
        email_enabled: true
      });
      setSelectedCampaignForAlert("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create alert");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  const handleCheckAlerts = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/alerts/check/${campaignId}`);
      if (response.data.triggered_alerts.length > 0) {
        toast.success(`${response.data.triggered_alerts.length} alert(s) triggered!`);
      } else {
        toast.info("No alerts triggered");
      }
    } catch (error) {
      toast.error("Failed to check alerts");
    }
  };

  const getAiBudgetSuggestion = async () => {
    setLoadingAi(true);
    try {
      const response = await axios.post(`${API}/ai/budget-suggestion`, {
        industry: "general",
        goal: newCampaign.bid_type === "cpa" ? "conversions" : "brand_awareness",
        duration_days: 30
      });
      setAiSuggestion(response.data);
    } catch (error) {
      toast.error("Failed to get AI suggestion");
    } finally {
      setLoadingAi(false);
    }
  };

  const applyTemplate = (template) => {
    setNewCampaign(prev => ({
      ...prev,
      name: `${template.name} Campaign`,
      bid_type: template.bid_type,
      bid_amount: template.suggested_bid,
      targeting: template.targeting
    }));
    toast.success(`Applied "${template.name}" template`);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="advertiser-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
            </Link>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-night-paper border border-night-subtle">
                <DollarSign className="w-4 h-4 text-emerald" />
                <span className="font-mono text-white">${user?.balance?.toFixed(2) || "0.00"}</span>
              </div>

              <Button 
                onClick={() => setShowFundModal(true)}
                className="bg-emerald hover:bg-emerald/90"
                data-testid="fund-account-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Fund Account
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-night-paper transition-colors">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-night-paper border-night-subtle">
                  <DropdownMenuItem className="text-muted-foreground">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-rose" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white mb-1">
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-muted-foreground">
              Manage your campaigns and track performance
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/analytics">
              <Button 
                variant="outline"
                className="border-night-subtle"
                data-testid="view-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </Button>
            </Link>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-primary hover:bg-indigo-hover btn-glow"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Spend", value: `$${analytics?.total_spend?.toFixed(2) || "0.00"}`, icon: DollarSign, color: "text-primary" },
            { label: "Impressions", value: analytics?.total_impressions?.toLocaleString() || "0", icon: Eye, color: "text-emerald" },
            { label: "Clicks", value: analytics?.total_clicks?.toLocaleString() || "0", icon: MousePointer, color: "text-yellow-500" },
            { label: "CTR", value: `${analytics?.ctr || 0}%`, icon: TrendingUp, color: "text-rose" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 rounded-lg bg-night-paper border border-night-subtle card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`stat-${stat.label.toLowerCase().replace(" ", "-")}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="font-heading text-2xl font-bold text-white">{stat.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Campaigns Table */}
        <div className="rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
          <div className="p-6 border-b border-night-subtle">
            <h2 className="font-heading text-lg font-semibold text-white">Your Campaigns</h2>
          </div>

          {campaigns.length === 0 ? (
            <div className="p-12 text-center">
              <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">Create your first campaign to start advertising</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-primary hover:bg-indigo-hover">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Budget</th>
                    <th>Spend</th>
                    <th>Impressions</th>
                    <th>CTR</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                      <td>
                        <div className="font-semibold text-white">{campaign.name}</div>
                        <div className="text-xs text-muted-foreground">{campaign.bid_type.toUpperCase()}</div>
                      </td>
                      <td>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          campaign.status === "active" ? "bg-emerald/20 text-emerald" :
                          campaign.status === "paused" ? "bg-yellow-500/20 text-yellow-500" :
                          "bg-night-subtle text-muted-foreground"
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="font-mono">${campaign.budget?.toFixed(2)}</td>
                      <td className="font-mono">${campaign.spend?.toFixed(2)}</td>
                      <td>{campaign.impressions?.toLocaleString()}</td>
                      <td>
                        {campaign.impressions > 0 
                          ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2)
                          : "0.00"}%
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {campaign.status === "active" ? (
                            <button 
                              onClick={() => handleUpdateStatus(campaign.id, "paused")}
                              className="p-2 rounded hover:bg-night-subtle text-yellow-500"
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleUpdateStatus(campaign.id, "active")}
                              className="p-2 rounded hover:bg-night-subtle text-emerald"
                              title="Activate"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button 
                            onClick={() => {
                              setSelectedCampaignForAlert(campaign.id);
                              setShowAlertModal(true);
                            }}
                            className="p-2 rounded hover:bg-night-subtle text-primary"
                            title="Set Alert"
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="p-2 rounded hover:bg-night-subtle text-rose"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Performance Alerts Section */}
        <div className="mt-8 rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
          <div className="p-6 border-b border-night-subtle flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BellRing className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-lg font-semibold text-white">Performance Alerts</h2>
            </div>
            <Button 
              size="sm"
              onClick={() => setShowAlertModal(true)}
              className="bg-primary hover:bg-indigo-hover"
              disabled={campaigns.length === 0}
              data-testid="create-alert-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Alert
            </Button>
          </div>

          {alerts.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">No alerts configured</h3>
              <p className="text-muted-foreground text-sm">
                Set up alerts to get notified when campaigns need attention
              </p>
            </div>
          ) : (
            <div className="divide-y divide-night-subtle">
              {alerts.map((alert) => {
                const campaign = campaigns.find(c => c.id === alert.campaign_id);
                return (
                  <div key={alert.id} className="p-4 flex items-center justify-between" data-testid={`alert-${alert.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        alert.is_active ? "bg-primary/20" : "bg-night-subtle"
                      }`}>
                        <Bell className={`w-5 h-5 ${alert.is_active ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <div>
                        <div className="font-semibold text-white">
                          {alert.alert_type.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {campaign?.name || "Unknown Campaign"} · Threshold: {alert.threshold}
                          {alert.alert_type === "budget_depleted" || alert.alert_type === "low_ctr" ? "%" : ""}
                        </div>
                        {alert.webhook_url && (
                          <div className="flex items-center gap-1 text-xs text-primary mt-1">
                            <Webhook className="w-3 h-3" />
                            Webhook configured
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {alert.triggered_count > 0 && (
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Triggered</div>
                          <div className="font-semibold text-yellow-500">{alert.triggered_count}x</div>
                        </div>
                      )}
                      <button
                        onClick={() => handleCheckAlerts(alert.campaign_id)}
                        className="p-2 rounded hover:bg-night-subtle text-emerald"
                        title="Check Now"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAlert(alert.id)}
                        className="p-2 rounded hover:bg-night-subtle text-rose"
                        title="Delete Alert"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-night-paper border-night-subtle max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Create Campaign</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-night-bg">
              <TabsTrigger value="manual" data-testid="manual-tab">Manual Setup</TabsTrigger>
              <TabsTrigger value="templates" data-testid="templates-tab">Use Template</TabsTrigger>
            </TabsList>

            <TabsContent value="templates" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="p-4 rounded-lg bg-night-bg border border-night-subtle text-left hover:border-primary transition-colors"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="font-semibold text-white mb-1">{template.name}</div>
                    <div className="text-xs text-muted-foreground mb-2">{template.description}</div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-primary/20 text-primary">
                        {template.bid_type.toUpperCase()}
                      </span>
                      <span className="text-muted-foreground">${template.suggested_bid}/bid</span>
                    </div>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="manual" className="mt-4">
              <form onSubmit={handleCreateCampaign} className="space-y-4">
                <div className="space-y-2">
                  <Label>Campaign Name</Label>
                  <Input
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="My Awesome Campaign"
                    required
                    className="bg-night-bg border-night-subtle"
                    data-testid="campaign-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Budget ($)</Label>
                    <Input
                      type="number"
                      value={newCampaign.budget}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, budget: parseFloat(e.target.value) }))}
                      min="10"
                      step="10"
                      required
                      className="bg-night-bg border-night-subtle"
                      data-testid="budget-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Daily Budget ($)</Label>
                    <Input
                      type="number"
                      value={newCampaign.daily_budget}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, daily_budget: parseFloat(e.target.value) }))}
                      min="1"
                      step="1"
                      className="bg-night-bg border-night-subtle"
                      data-testid="daily-budget-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bid Type</Label>
                    <Select
                      value={newCampaign.bid_type}
                      onValueChange={(value) => setNewCampaign(prev => ({ ...prev, bid_type: value }))}
                    >
                      <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="bid-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-night-paper border-night-subtle">
                        <SelectItem value="cpm">CPM (Cost per 1000 impressions)</SelectItem>
                        <SelectItem value="cpc">CPC (Cost per click)</SelectItem>
                        <SelectItem value="cpa">CPA (Cost per action)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bid Amount ($)</Label>
                    <Input
                      type="number"
                      value={newCampaign.bid_amount}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, bid_amount: parseFloat(e.target.value) }))}
                      min="0.01"
                      step="0.01"
                      required
                      className="bg-night-bg border-night-subtle"
                      data-testid="bid-amount-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={newCampaign.start_date}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, start_date: e.target.value }))}
                      required
                      className="bg-night-bg border-night-subtle"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newCampaign.end_date}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, end_date: e.target.value }))}
                      className="bg-night-bg border-night-subtle"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>

                {/* AI Budget Suggestion */}
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-white">AI Budget Suggestion</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={getAiBudgetSuggestion}
                      disabled={loadingAi}
                      className="border-primary/50 text-primary hover:bg-primary/20"
                      data-testid="ai-suggest-btn"
                    >
                      {loadingAi ? (
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Get Suggestion"
                      )}
                    </Button>
                  </div>
                  {aiSuggestion && (
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {aiSuggestion.suggestion}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="border-night-subtle"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-indigo-hover" data-testid="submit-campaign-btn">
                    Create Campaign
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Fund Account Modal */}
      <Dialog open={showFundModal} onOpenChange={setShowFundModal}>
        <DialogContent className="bg-night-paper border-night-subtle">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Fund Your Account</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-night-bg border border-night-subtle">
              <div className="text-sm text-muted-foreground mb-1">Current Balance</div>
              <div className="font-heading text-3xl font-bold text-white">
                ${user?.balance?.toFixed(2) || "0.00"}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Amount</Label>
              <div className="grid grid-cols-4 gap-2">
                {[50, 100, 250, 500].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFundAmount(amount)}
                    className={`p-3 rounded-lg border font-semibold transition-colors ${
                      fundAmount === amount
                        ? "border-primary bg-primary/20 text-white"
                        : "border-night-subtle bg-night-bg text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`fund-amount-${amount}`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Custom Amount</Label>
              <Input
                type="number"
                value={fundAmount}
                onChange={(e) => setFundAmount(parseFloat(e.target.value))}
                min="10"
                step="10"
                className="bg-night-bg border-night-subtle"
                data-testid="custom-fund-input"
              />
            </div>

            <Button
              onClick={handleFundAccount}
              className="w-full bg-emerald hover:bg-emerald/90"
              data-testid="proceed-payment-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Pay ${fundAmount.toFixed(2)} with Stripe
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Secure payment powered by Stripe. Funds are added instantly upon successful payment.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Alert Modal */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="bg-night-paper border-night-subtle">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <BellRing className="w-5 h-5 text-primary" />
              Create Performance Alert
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateAlert} className="space-y-4">
            <div className="space-y-2">
              <Label>Campaign</Label>
              <Select
                value={selectedCampaignForAlert}
                onValueChange={setSelectedCampaignForAlert}
              >
                <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="alert-campaign-select">
                  <SelectValue placeholder="Select a campaign" />
                </SelectTrigger>
                <SelectContent className="bg-night-paper border-night-subtle">
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Alert Type</Label>
              <Select
                value={newAlert.alert_type}
                onValueChange={(value) => setNewAlert(prev => ({ ...prev, alert_type: value }))}
              >
                <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="alert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-night-paper border-night-subtle">
                  <SelectItem value="budget_depleted">Budget Depleted (%)</SelectItem>
                  <SelectItem value="low_ctr">Low CTR (%)</SelectItem>
                  <SelectItem value="high_spend">High Daily Spend ($)</SelectItem>
                  <SelectItem value="goal_reached">Conversion Goal Reached</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Threshold</Label>
              <Input
                type="number"
                value={newAlert.threshold}
                onChange={(e) => setNewAlert(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
                min="0"
                step="1"
                required
                className="bg-night-bg border-night-subtle"
                data-testid="alert-threshold-input"
              />
              <p className="text-xs text-muted-foreground">
                {newAlert.alert_type === "budget_depleted" && "Alert when budget usage exceeds this percentage"}
                {newAlert.alert_type === "low_ctr" && "Alert when CTR falls below this percentage"}
                {newAlert.alert_type === "high_spend" && "Alert when daily spend exceeds this amount"}
                {newAlert.alert_type === "goal_reached" && "Alert when conversions reach this number"}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Webhook URL (Optional)</Label>
              <Input
                type="url"
                value={newAlert.webhook_url}
                onChange={(e) => setNewAlert(prev => ({ ...prev, webhook_url: e.target.value }))}
                placeholder="https://your-server.com/webhook"
                className="bg-night-bg border-night-subtle"
                data-testid="alert-webhook-input"
              />
              <p className="text-xs text-muted-foreground">
                Receive POST requests with alert data when triggered
              </p>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-night-bg border border-night-subtle">
              <div>
                <div className="font-semibold text-white">Email Notifications</div>
                <div className="text-sm text-muted-foreground">Get email alerts when triggered</div>
              </div>
              <Switch
                checked={newAlert.email_enabled}
                onCheckedChange={(checked) => setNewAlert(prev => ({ ...prev, email_enabled: checked }))}
                data-testid="alert-email-switch"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAlertModal(false)}
                className="border-night-subtle"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-indigo-hover" data-testid="submit-alert-btn">
                <Bell className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvertiserDashboard;
